/**
 * usePlanLimits — reactive composable for subscription plan feature gating.
 *
 * Shares a module-level `plans` ref across all callers so the fetch happens
 * once per app session (same behaviour as the backend TTL cache).
 *
 * Usage:
 *   const { loadPlans, isFeatureEnabled, canStartSkillsCheck } = usePlanLimits();
 *   await loadPlans();                    // call once on app init / component mount
 *   isFeatureEnabled('revisionMode')      // checks current parent plan
 *   canStartSkillsCheck(childId)          // checks monthly quota
 */

import { ref, computed } from 'vue';
import { getPlans } from '@/services/planService';
import { useUserStore } from '@/stores/userStore';
import { useProgressStore } from '@/stores/progressStore';

// ─── Module-level singleton state ────────────────────────────────────────────

const _plans = ref({});
let _loading = false;
let _loaded = false;

// Conservative limits used only while the plan list is still loading.
// These match free-tier behaviour so no paid feature is accidentally unlocked
// before the API response arrives. Not a plan definition — just a safe default.
const LOADING_LIMITS = {
  maxChildren:              1,
  maxLessonsPerDay:         5,
  maxRevisionsPerWeek:      null,
  maxSkillsChecksPerMonth:  1,
  revisionMode:             false,
  pdfExport:                false,
  fullMetrics:              false,
};

// ─── Composable ──────────────────────────────────────────────────────────────

export function usePlanLimits() {
  const userStore = useUserStore();
  const progressStore = useProgressStore();

  /** The plan the parent currently has access to (accounts for cancellation + renewsAt). */
  const currentPlan = computed(() => userStore.effectivePlan || 'free');

  /** Limits object for the current plan. Returns LOADING_LIMITS until plans are fetched. */
  const limits = computed(() => _plans.value[currentPlan.value]?.limits ?? LOADING_LIMITS);

  /**
   * Fetch plans from the API (no-op if already loaded).
   * Safe to call from multiple components — only one fetch occurs.
   */
  async function loadPlans(forceRefresh = false) {
    if (_loaded && !forceRefresh) return;
    if (_loading) return;
    _loading = true;
    try {
      const map = await getPlans(forceRefresh);
      _plans.value = map;
      _loaded = true;
    } finally {
      _loading = false;
    }
  }

  /**
   * Return limits for any plan by ID (useful when rendering plan comparison
   * tables or checking a different plan's features).
   *
   * @param {string} planId
   * @returns {object}
   */
  function limitsFor(planId) {
    return _plans.value[planId]?.limits ?? LOADING_LIMITS;
  }

  /**
   * Check a boolean feature flag for the current plan.
   * Supported keys: revisionMode, pdfExport, fullMetrics
   *
   * @param {string} featureKey
   * @returns {boolean}
   */
  function isFeatureEnabled(featureKey) {
    return limits.value[featureKey] === true;
  }

  /**
   * Get the max number of children allowed by the current plan.
   * Returns 1 (free default) if limits are not yet loaded.
   *
   * @returns {number}
   */
  function getMaxChildren() {
    return limits.value.maxChildren ?? 1;
  }

  /**
   * Count how many diagnostic/skills-check sessions this child has done
   * in the current calendar month.
   *
   * @param {string} childId
   * @returns {number}
   */
  function diagnosticsThisMonth(childId) {
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
    const log = progressStore.activityLog[childId] || [];
    return log.filter(e => e.type === 'diagnostic' && (e.timestamp || 0) >= monthStart).length;
  }

  /**
   * Returns true if the child is allowed to start a new skills check
   * based on the plan's maxSkillsChecksPerMonth limit.
   * null/undefined limit = unlimited.
   *
   * @param {string} childId
   * @returns {boolean}
   */
  function canStartSkillsCheck(childId) {
    const max = limits.value.maxSkillsChecksPerMonth;
    if (max === null || max === undefined) return true;
    return diagnosticsThisMonth(childId) < max;
  }

  /**
   * Returns true if the child is allowed to do more revisions this week.
   * null/undefined = unlimited.
   *
   * @param {string} childId
   * @returns {boolean}
   */
  function canStartRevision(childId) {
    if (!isFeatureEnabled('revisionMode')) return false;
    const maxPerWeek = limits.value.maxRevisionsPerWeek;
    if (maxPerWeek === null || maxPerWeek === undefined) return true;
    // Count revisions this week
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Monday start
    const weekStart = new Date(now.getFullYear(), now.getMonth(), diff);
    weekStart.setHours(0, 0, 0, 0);
    const log = progressStore.activityLog[childId] || [];
    const revisionsThisWeek = log.filter(
      e => e.type === 'revision' && (e.timestamp || 0) >= weekStart.getTime()
    ).length;
    return revisionsThisWeek < maxPerWeek;
  }

  return {
    plans: _plans,
    limits,
    currentPlan,
    loadPlans,
    limitsFor,
    isFeatureEnabled,
    getMaxChildren,
    diagnosticsThisMonth,
    canStartSkillsCheck,
    canStartRevision,
  };
}
